import type {
  CopyTemplateToGroupsApiResponse,
  TemplateCopyTargetApi,
  TemplateDetailApi,
  TemplateOverrideAvailabilityApi,
} from '@/types/api/templates';
import type { CopyTemplateToGroupsVariables } from '@/hooks/useTemplatesData';
import type { Template } from '@/types/template';

/**
 * Demo-mode fabricated response for creating a template.
 * Used as the demoFn for useCreateTemplate.
 */
export async function createTemplateDemoResponse(data: Template): Promise<TemplateDetailApi> {
  return {
    id: Date.now(),
    name: data.name,
    templateTypeId: data.type === 'statement' ? 2 : 1,
    templateTypeName: data.type === 'statement' ? 'Statement' : 'Email',
    subject: data.subject,
    content: data.content,
    isDefault: data.isDefault,
    category: data.category?.join(', '),
    variables: data.variables?.join(', '),
    createdAt: new Date().toISOString(),
    createdByUserName: 'Current User',
  } as TemplateDetailApi;
}

/**
 * Factory for the demo-mode update-template response, capturing the templateId
 * closure. Returns the demoFn for useUpdateTemplate.
 */
export function makeUpdateTemplateDemoFn(templateId: string) {
  return async (): Promise<TemplateDetailApi> => {
    return {
      id: Number(templateId),
      name: 'Updated Template',
      templateTypeId: 1,
      templateTypeName: 'Email',
      content: '',
      isDefault: false,
      createdAt: new Date().toISOString(),
    } as TemplateDetailApi;
  };
}

/**
 * Demo-mode fabricated response for creating a company override of a group template.
 * Used as the demoFn for useCreateTemplateOverride.
 */
export async function createTemplateOverrideDemoResponse(templateId: string): Promise<TemplateDetailApi> {
  return {
    id: Date.now(),
    name: 'Company copy',
    sourceTemplateId: Number(templateId),
    templateTypeId: 1,
    templateTypeName: 'Email',
    content: '',
    isDefault: false,
    createdAt: new Date().toISOString(),
  } as TemplateDetailApi;
}

/**
 * Demo-mode fabricated response for setting a template as default.
 * Used as the demoFn for useSetDefaultTemplate.
 */
export async function setDefaultTemplateDemoResponse(): Promise<TemplateDetailApi> {
  return {
    id: 0,
    name: '',
    templateTypeId: 2,
    templateTypeName: 'Statement',
    content: '',
    isDefault: true,
    createdAt: new Date().toISOString(),
  } as TemplateDetailApi;
}

/**
 * Demo-mode fabricated response for clearing a template's default flag.
 * Used as the demoFn for useClearDefaultTemplate.
 */
export async function clearDefaultTemplateDemoResponse(): Promise<TemplateDetailApi> {
  return {
    id: 0,
    name: '',
    templateTypeId: 2,
    templateTypeName: 'Statement',
    content: '',
    isDefault: false,
    createdAt: new Date().toISOString(),
  } as TemplateDetailApi;
}

/**
 * Demo-mode company groups a template can be copied into.
 * Used as the demo data for useTemplateCopyTargets.
 */
export const templateCopyTargetsDemoData: TemplateCopyTargetApi[] = [
  { companyGroupId: 201, name: 'Northern Division', companyCount: 3 },
  { companyGroupId: 202, name: 'Retail Group', companyCount: 1 },
];

/**
 * Demo-mode company group shape: several companies, so customising a template is meaningful.
 * Used as the demo data for useTemplateOverrideAvailability.
 */
export const templateOverrideAvailabilityDemoData: TemplateOverrideAvailabilityApi = {
  canCreateOverride: true,
  companyCount: 3,
};

/**
 * Demo-mode fabricated response for copying a template into other company groups.
 * Used as the demoFn for useCopyTemplateToGroups.
 */
export async function copyTemplateToGroupsDemoResponse(
  variables: CopyTemplateToGroupsVariables,
): Promise<CopyTemplateToGroupsApiResponse> {
  // One target collides so demo mode shows the partial-success path the API can return.
  const collidingCompanyGroupId = 202;

  return {
    results: variables.companyGroupIds.map((companyGroupId) =>
      companyGroupId === collidingCompanyGroupId
        ? {
            companyGroupId,
            templateId: null,
            copied: false,
            skipReason: 'A template with that name already exists in this group',
          }
        : {
            companyGroupId,
            templateId: Date.now() + companyGroupId,
            copied: true,
            skipReason: null,
          },
    ),
  };
}
